import { z } from 'zod';
import type { FeatureFlags } from '../features.js';

/**
 * THE FEATURE FLAGS' VALIDATION SCHEMA, ITS OWN FILE.
 *
 * Lifted out of `settings.schema.ts` for the reason `check:filesize` gives
 * when it refuses an addition to an allowlisted file: put it in a new file
 * rather than raise a ceiling. Four keys landed here at once (2026-09-19 —
 * `erp`, `labourSuppliers`, `legalEntities`, `deviceApprovals`, each for a nav
 * row no setting could reach) and that was the sixteen lines that tipped it.
 *
 * The seam is real rather than convenient: the KEYS, the `FeatureFlags` type
 * and the presets are plain data in `../features.js`, deliberately zod-free so
 * the web boot path can read them without loading zod. This file is the one
 * place that puts zod around them, which is exactly one concern.
 *
 * EVERY KEY DEFAULTS TRUE, and that is load-bearing rather than tidy.
 * `SettingsService.getAll` parses the STORED settings blob through this
 * schema, so a key added here is filled in as `true` for every company whose
 * saved flags predate it. That is the whole reason a new nav gate is invisible
 * to a tenant already running: they never stored the key, so they read the
 * default. A key defaulting FALSE would remove a screen from a live
 * deployment on deploy day, with nothing connecting the disappearance to the
 * change that caused it.
 */
export const FeatureFlagsSchema = z.object({
  leave: z.boolean().default(true), // leave requests, approvals, calendar, types, holidays
  corrections: z.boolean().default(true), // attendance correction requests
  announcements: z.boolean().default(true),
  letters: z.boolean().default(true), // HR letters (salary cert / NOC / experience)
  payroll: z.boolean().default(true), // payroll prep + WPS export
  reports: z.boolean().default(true), // the Reports hub
  shifts: z.boolean().default(true), // shifts + schedules
  team: z.boolean().default(true), // team directory + team attendance (managers)
  orgStructure: z.boolean().default(true), // departments + designations
  documents: z.boolean().default(true), // worker document store + expiry
  faceRecognition: z.boolean().default(true), // field face enrollment
  gratuity: z.boolean().default(true), // end-of-service gratuity calculator
  lifecycle: z.boolean().default(true), // onboarding / offboarding
  dailyRoster: z.boolean().default(true), // camp muster roster — bulk manual attendance entry for a whole camp
  selfService: z.boolean().default(true), // personal "My…" self-service screens (check-in/out, my attendance, profile). Off = management-console-only web (login-less site workers punch via the field APK/kiosk).
  workers: z.boolean().default(true), // does the company employ login-less site workers at all — the whole site-workforce pillar (field/kiosk capture, worker roster, site/camp boards & reports). Off = office-only tenant; existing worker data is soft-hidden, never deleted.
  camps: z.boolean().default(true), // does the company run labour camps at all — the whole camp pillar (occupancy/capacity boards, camp muster roster, camp<->site journey surfaces). Off = attendance-only tenant that checks in/out at SITES; existing CAMP worksites and their recorded history are soft-hidden, never deleted, and NO stored or recomputed attendance figure changes.
  // A camp tablet's admitted punch moves the worker's camp to that camp from
  // that company day (owner, 2026-11-02: "make auto transfer with morning
  // dispatch"). Read by `campAutoTransfer.ts`; inert while `camps` is off.
  autoCampTransfer: z.boolean().default(true),
  hardwareTerminals: z.boolean().default(true), // standalone ZKTeco/Hikvision face terminals (ADMS/ISAPI push). Off = registering new ones is refused server-side too; already-registered terminals and the attendance they recorded are untouched.
  // Does this company exchange master data and clock cards with an ERP at all.
  // NOT the same switch as `erp.enabled`, which says whether the CONNECTION is
  // live: a company that does ERP with the connection paused still has staged
  // rows waiting for a decision, and hiding the queue would strand them.
  erp: z.boolean().default(true),
  // Does this company source labour through supplier companies. Off = an
  // employer of its own people only; existing supplier records are soft-hidden.
  labourSuppliers: z.boolean().default(true),
  // Does this company operate as more than one legal entity (Company branches).
  // Off = a single-entity tenant, for whom the screen asks a question with one
  // answer.
  legalEntities: z.boolean().default(true),
  // The device registration/approval queue. Off = a tenant with no capture
  // hardware of any kind — no field tablets, no panels, no terminals.
  deviceApprovals: z.boolean().default(true),
  // Staff tasks — assign, hand in, review, one shared versioned file. OPT-IN:
  // defaults FALSE so it reaches a company when its administrator turns it on
  // (`OPT_IN_FEATURES` in features.ts), not on the day the server updates.
  tasks: z.boolean().default(false),
  // Morning dispatch (which worker goes to which site today). Built, NOT yet
  // released: it defaults FALSE, the one exception to the rule above, because
  // here a true default would ADD an unreleased screen to every tenant rather
  // than keep an existing one. Listed in `UNRELEASED_FEATURES`.
  dispatch: z.boolean().default(false),
});
/**
 * The schema and the hand-written `FeatureFlags` must stay in step. These two
 * assignments are mutual assignability checks: add a flag to the schema without
 * adding it to `FEATURE_KEYS` (or vice versa) and typecheck fails here rather
 * than at some call site later.
 */
const _flagsFromSchema: FeatureFlags = {} as z.infer<typeof FeatureFlagsSchema>;
const _flagsToSchema: z.infer<typeof FeatureFlagsSchema> = {} as FeatureFlags;
void _flagsFromSchema;
void _flagsToSchema;
